const DIAGNOSTIC_STATUS_LABELS = {
  absent: "Diagnostics absents",
  running: "Diagnostics en cours",
  partial: "Diagnostics partiels",
  failed: "Diagnostics échoués",
  available: "Diagnostics disponibles",
};

const SHOWN_DISPLAY_STATES = new Set([
  "shown",
  "visible",
  "displayed",
  "display",
  "affiche",
  "affichee",
  "affichees",
  "true",
]);

const HIDDEN_DISPLAY_STATES = new Set([
  "hidden",
  "not_shown",
  "not-shown",
  "not_displayed",
  "not-displayed",
  "unshown",
  "masque",
  "masquee",
  "masquees",
  "non_affiche",
  "non_affichee",
  "non_affichees",
  "false",
]);

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeKey(value) {
  return normalizeText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function isRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value);
}

function hasAnySummaryValue(summary) {
  return [
    "examined_order_count",
    "shown_order_count",
    "not_shown_order_count",
    "examined_block_count",
    "shown_block_count",
    "hidden_block_count",
  ].some((key) => summary[key] !== undefined && summary[key] !== null);
}

function normalizeDiagnosticState(value, {
  analysisActive = false,
  analysisComplete = true,
  analysisStatus = "",
  hasPayload = false,
  hasRows = false,
  hasSummary = false,
} = {}) {
  const normalized = normalizeKey(value);
  if (["failed", "failure", "error", "errored", "echoue", "echec"].includes(normalized)) {
    return "failed";
  }
  if (["running", "pending", "in_progress", "processing", "en_cours"].includes(normalized)) {
    return "running";
  }
  if (["partial", "partiel", "incomplete", "incomplet"].includes(normalized)) {
    return "partial";
  }
  if (["available", "complete", "completed", "done", "ready", "available_details"].includes(normalized)) {
    return "available";
  }

  const normalizedAnalysisStatus = normalizeKey(analysisStatus);
  if (["failed", "failure", "error", "errored", "echoue", "echec"].includes(normalizedAnalysisStatus)) {
    return "failed";
  }
  if (["cancelled", "canceled", "interrupted", "annule"].includes(normalizedAnalysisStatus)) {
    return hasRows || hasSummary ? "partial" : "absent";
  }
  if (analysisActive) {
    return "running";
  }
  if (!analysisComplete && hasPayload) {
    return hasRows || hasSummary ? "partial" : "running";
  }
  if (hasRows || hasSummary) {
    return "available";
  }
  return "absent";
}

/**
 * Keep the UI tolerant of old, missing or partially populated diagnostics.
 * A missing list is an unavailable diagnostic, never an empty infeasibility
 * result.
 */
export function normalizePullForwardDiagnostics(value, context = {}) {
  const payload = isRecord(value) ? value : null;
  const summary = isRecord(payload?.summary) ? payload.summary : {};
  const blocks = Array.isArray(payload?.blocks)
    ? payload.blocks.filter(isRecord)
    : [];
  const orders = Array.isArray(payload?.orders)
    ? payload.orders.filter(isRecord)
    : [];
  const rows = blocks.length ? blocks : orders;
  const hasSummary = hasAnySummaryValue(summary);
  const state = normalizeDiagnosticState(
    payload?.status || payload?.state || payload?.diagnostic_status,
    {
      ...context,
      hasPayload: Boolean(payload),
      hasRows: rows.length > 0,
      hasSummary,
    },
  );

  return {
    hasPayload: Boolean(payload),
    hasSummary,
    blocks,
    orders,
    rows,
    usesBlocks: blocks.length > 0,
    raw: payload,
    state,
    stateLabel: DIAGNOSTIC_STATUS_LABELS[state],
    summary,
  };
}

export function diagnosticCount(summary, key) {
  const number = Number(summary?.[key]);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

export function diagnosticOrderDisplayState(order) {
  const value = normalizeKey(
    order?.display_state
      ?? order?.displayState
      ?? order?.visibility
      ?? order?.shown,
  );
  if (SHOWN_DISPLAY_STATES.has(value)) {
    return "shown";
  }
  if (HIDDEN_DISPLAY_STATES.has(value)) {
    return "hidden";
  }
  return "unknown";
}

export function filterPullForwardDiagnosticsOrders(orders, { filter = "all", query = "" } = {}) {
  const normalizedFilter = normalizeKey(filter || "all");
  const normalizedQuery = normalizeKey(query).replace(/_/g, " ");
  const list = Array.isArray(orders) ? orders : [];
  return list.filter((order) => {
    const displayState = diagnosticOrderDisplayState(order);
    if (normalizedFilter === "shown" && displayState !== "shown") {
      return false;
    }
    if (normalizedFilter === "hidden" && displayState !== "hidden") {
      return false;
    }
    if (!normalizedQuery) {
      return true;
    }
    const searchableText = [
      order?.order_id,
      ...(Array.isArray(order?.order_ids) ? order.order_ids : []),
      order?.client_name,
      order?.address,
      order?.delivery_address,
      order?.delivery_label,
      order?.loading_address,
      order?.loading_label,
      order?.loading_client_name,
      order?.grain_type,
      order?.product,
    ]
      .map((value) => normalizeKey(value).replace(/_/g, " "))
      .filter(Boolean)
      .join(" ");
    return searchableText.includes(normalizedQuery);
  });
}

// The legacy function name is kept because older consumers still pass order
// rows. New candidate payloads use the same filtering contract with one row per
// atomic block.
export const filterPullForwardDiagnosticsRows = filterPullForwardDiagnosticsOrders;

const PULL_FORWARD_DIAGNOSTIC_GROUP_DEFINITIONS = [
  {
    key: "shown",
    label: "Conservées dans les propositions",
  },
  {
    key: "hidden",
    label: "Écartées des propositions",
  },
  {
    key: "unknown",
    label: "Visibilité non documentée",
  },
];

export function groupPullForwardDiagnosticsOrders(orders) {
  const groupedOrders = {
    shown: [],
    hidden: [],
    unknown: [],
  };
  const list = Array.isArray(orders) ? orders : [];
  list.forEach((order) => {
    groupedOrders[diagnosticOrderDisplayState(order)].push(order);
  });
  return PULL_FORWARD_DIAGNOSTIC_GROUP_DEFINITIONS
    .map((group) => ({ ...group, orders: groupedOrders[group.key] }))
    .filter((group) => group.orders.length > 0);
}

export const groupPullForwardDiagnosticsBlocks = groupPullForwardDiagnosticsOrders;

export function diagnosticStatusDescription(state) {
  switch (state) {
    case "running":
      return "Les lignes et compteurs peuvent encore évoluer pendant l’analyse. Elles ne permettent pas de conclure à l’infaisabilité.";
    case "partial":
      return "Une partie seulement des commandes a été documentée. Les commandes absentes ne sont pas déclarées infaisables.";
    case "failed":
      return "Le diagnostic n’a pas pu être produit complètement. Cet échec technique ne constitue pas une preuve d’infaisabilité.";
    case "available":
      return "Le diagnostic décrit les commandes examinées par cette analyse et la décision de visibilité de chaque ligne.";
    case "absent":
    default:
      return "Aucun détail de diagnostic n’est fourni pour cette analyse. Son absence ne constitue pas une preuve d’infaisabilité.";
  }
}

export const PULL_FORWARD_DIAGNOSTIC_FILTERS = [
  { key: "all", label: "Toutes" },
  { key: "shown", label: "Affichées" },
  { key: "hidden", label: "Non affichées" },
];

export const PULL_FORWARD_DIAGNOSTIC_SUMMARY_KEYS = [
  "examined_order_count",
  "shown_order_count",
  "not_shown_order_count",
  "examined_block_count",
  "shown_block_count",
  "hidden_block_count",
];
